import React, { useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LogSheetTitle from './LogSheetTitle';
import colors from '../../assets/theme/colors';

const LogFormShell = ({
  open,
  onClose,
  title,
  subtitle,
  titleBadge,
  titleLeadingContent,
  headerContent,
  compactTitle = false,
  titleWeight,
  titleSx,
  subtitleSx,
  titleBadgeSx,
  closeButtonSx,
  closeIcon,
  closeIconSx,
  hideHeader = false,
  children,
  footer,
  bodySx: bodySxOverride = {},
  surfaceSx: surfaceSxOverride = {},
  headerSx: headerSxOverride = {},
  mobileBreakpoint = 'md',
  maxWidth = 'sm',
  forceDrawer = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  const useDrawer = forceDrawer || isMobile;
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const scrollNode = bodyRef.current;
    if (!scrollNode) {
      return;
    }

    requestAnimationFrame(() => {
      if (typeof scrollNode.scrollTo === "function") {
        scrollNode.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      scrollNode.scrollTop = 0;
    });
  }, [open]);

  const surfaceSx = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100vw',
    boxSizing: 'border-box',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    ...(isMobile
      ? {
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          height: '85vh',
          maxHeight: '85vh',
        }
      : {
          borderRadius: 3,
          height: 'min(88dvh, 88vh)',
          maxHeight: 'min(88dvh, 88vh)',
    }),
    ...surfaceSxOverride,
    ...(isMobile
      ? {
          borderRadius: 0,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          '&.MuiPaper-root': {
            borderRadius: 0,
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }
      : {}),
  };

  const headerSx = {
    flex: 'none',
    px: { xs: 3, sm: 4 },
    pt: { xs: 2.5, sm: 3 },
    pb: 2.25,
    borderBottom: `1px solid ${colors.app.cards.border}`,
    ...headerSxOverride,
  };

  const bodyStyles = {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    px: { xs: 1.5, sm: 4 },
    pt: 2.75,
    pb: 2.5,
  };

  const footerSx = {
    flex: 'none',
    px: { xs: 1.5, sm: 4 },
    pb: { xs: 3, sm: 4 },
    pt: 0.5,
  };

  const contentSx = useDrawer
    ? {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderRadius: 0,
      }
    : surfaceSx;

  const content = (
    <Box className="log-form-shell-print" sx={contentSx}>
      {!hideHeader ? (
        <Box sx={headerSx}>
          <LogSheetTitle
            title={title}
            titleBadge={titleBadge}
            titleLeadingContent={titleLeadingContent}
            subtitle={subtitle}
            onClose={onClose}
            compactTitle={compactTitle}
            titleWeight={titleWeight}
            titleSx={titleSx}
            subtitleSx={subtitleSx}
            titleBadgeSx={titleBadgeSx}
            closeButtonSx={closeButtonSx}
            closeIcon={closeIcon}
            closeIconSx={closeIconSx}
          />
          {headerContent ? <Box sx={{ mt: 1.5 }}>{headerContent}</Box> : null}
        </Box>
      ) : null}
      <Box ref={bodyRef} sx={{ ...bodyStyles, ...bodySxOverride }}>{children}</Box>
      {footer ? <Box sx={footerSx}>{footer}</Box> : null}
    </Box>
  );

  if (useDrawer) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
          sx: {
            '& .MuiDrawer-paper.MuiPaper-root': {
              borderRadius: 0,
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        }}
        PaperProps={{
          sx: {
            ...surfaceSx,
            width: '100%',
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            overflowY: 'hidden',
            height: '85vh',
            maxHeight: '85vh',
            ...surfaceSxOverride,
            borderRadius: 0,
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            '&.MuiPaper-root': {
              borderRadius: 0,
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        }}
      >
        <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              width: 46,
              height: 5,
              borderRadius: 999,
              bgcolor: colors.semantic.ink3,
            }}
          />
        </Box>
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      keepMounted
      PaperProps={{
        sx: {
          ...surfaceSx,
          width: '100%',
          overflowY: 'hidden',
          ...surfaceSxOverride,
        },
      }}
    >
      {content}
    </Dialog>
  );
};

export default LogFormShell;
